import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { 
  getPendingInvitations, 
  acceptInvitation, 
  declineInvitation 
} from '../../services/invitationService';

const PendingInvitations = ({ onInvitationUpdate }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingInvitation, setProcessingInvitation] = useState(null);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const data = await getPendingInvitations();
      setInvitations(data);
    } catch (err) {
      console.error('Error fetching invitations:', err);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitation) => {
    setProcessingInvitation(invitation.id);
    try {
      const result = await acceptInvitation(invitation.invitation_token);
      
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      
      if (onInvitationUpdate) {
        onInvitationUpdate('accepted', invitation, result);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDeclineInvitation = async (invitation) => {
    setProcessingInvitation(invitation.id);
    try {
      await declineInvitation(invitation.invitation_token);
      
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      
      if (onInvitationUpdate) {
        onInvitationUpdate('declined', invitation);
      }
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err.response?.data?.message || 'Failed to decline invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatExpiresIn = (dateString) => {
    const expiryDate = new Date(dateString);
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Expired';
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffDays} days`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (invitations.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <GroupIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No pending invitations
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You'll see group invitations here when someone invites you.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {invitations.map((invitation) => (
          <Card key={invitation.id} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Invitation to join "{invitation.group_name}"
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Invited by {invitation.invited_by_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Sent on {formatDate(invitation.created_at)}
                  </Typography>
                </Box>
                <Chip 
                  label={formatExpiresIn(invitation.expires_at)}
                  size="small"
                  color={new Date(invitation.expires_at) <= new Date() ? 'error' : 'default'}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={() => handleAcceptInvitation(invitation)}
                  disabled={processingInvitation === invitation.id}
                  size="small"
                >
                  {processingInvitation === invitation.id ? 'Accepting...' : 'Accept'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => handleDeclineInvitation(invitation)}
                  disabled={processingInvitation === invitation.id}
                  size="small"
                >
                  {processingInvitation === invitation.id ? 'Declining...' : 'Decline'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default PendingInvitations;